import { useEffect, useRef, useState } from "react";
import { Building2, Check, ChevronDown, Plus } from "lucide-react";
import { useLocation } from "wouter";
import {
  useListMyPractices,
  useSetActivePractice,
  getListMyPracticesQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";

interface PracticeSwitcherProps {
  /** Optional Tailwind text-color class for the dropdown trigger label, used to match per-portal theming. */
  labelClassName?: string;
  /** Route to the practice settings page for the current portal (e.g. "/nurse/settings/practices"). */
  manageHref: string;
}

export function PracticeSwitcher({ labelClassName = "text-slate-700", manageHref }: PracticeSwitcherProps) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { data: practices, isLoading } = useListMyPractices();
  const setActive = useSetActivePractice();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 px-3 py-1.5">
        <Building2 className="h-4 w-4" />
        <span>Loading practice…</span>
      </div>
    );
  }

  const list = practices ?? [];
  const activeList = list.filter((p) => p.membershipStatus === "active");

  // If no practices, show a CTA to create one.
  if (activeList.length === 0) {
    return (
      <button
        onClick={() => setLocation(manageHref)}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 hover:border-slate-400 transition-colors"
      >
        <Plus className="h-4 w-4" />
        <span>Add practice</span>
      </button>
    );
  }

  const current = activeList.find((p) => p.isActive) ?? activeList[0];

  // Single practice — no dropdown, just show the name.
  if (activeList.length === 1) {
    return (
      <div className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg bg-slate-50 ${labelClassName}`}>
        <Building2 className="h-4 w-4 text-slate-500" />
        <span className="truncate max-w-[220px]">{current.name}</span>
      </div>
    );
  }

  const handleSwitch = async (practiceId: number) => {
    setOpen(false);
    if (practiceId === current.id) return;
    try {
      await setActive.mutateAsync({ data: { practiceId } });
      await queryClient.invalidateQueries({ queryKey: getListMyPracticesQueryKey() });
      // Invalidate practice-scoped data caches so the user sees the new context immediately.
      await queryClient.invalidateQueries();
    } catch {
      // toast notification — keep silent for now
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 text-sm font-medium px-3 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors ${labelClassName}`}
        data-testid="practice-switcher"
      >
        <Building2 className="h-4 w-4 text-slate-500" />
        <span className="truncate max-w-[180px]">{current.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-72 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-100">
            Your practices
          </div>
          <div className="max-h-72 overflow-y-auto">
            {activeList.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSwitch(p.id)}
                className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 text-left transition-colors"
                data-testid={`practice-option-${p.id}`}
              >
                <Building2 className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 truncate">{p.name}</p>
                  <p className="text-[11px] text-slate-400 capitalize">
                    {p.practiceType} · {p.membershipRole}
                  </p>
                </div>
                {p.isActive && <Check className="h-4 w-4 text-teal-600 shrink-0" />}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); setLocation(manageHref); }}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100 transition-colors"
          >
            <Plus className="h-4 w-4 text-slate-400" />
            Manage practices
          </button>
        </div>
      )}
    </div>
  );
}
